import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc, limit, startAfter, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import { rateLimiter } from '../utils/simpleRateLimiter';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { showErrorToast, showSuccessToast } from '../utils/toastUtils';
import DOMPurify from 'dompurify';
import '../css/BlogFeed.css';
import { loadFriends } from '../utils/friendService';

const POSTS_PER_PAGE = 10;

const BlogFeed = ({ refreshTrigger, filter = 'all' }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [friendsIds, setFriendsIds] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getAuth().currentUser);

  // Keep currentUser in sync with auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), user => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Load friends once when user is authenticated
  useEffect(() => {
    if (currentUser) {
      setFriendsLoading(true);
      // Apply rate limiting to friends loading
      if (!rateLimiter.canMakeRequest(currentUser.uid)) {
        showErrorToast("Too many friend list refreshes. Please wait a moment.");
        setFriendsLoading(false);
        return;
      }

      loadFriends(currentUser.uid)
        .then(friends => {
          const accepted = friends.filter(f => f.status === 'accepted');
          const ids = accepted.map(f => f.sender === currentUser.uid ? f.receiver : f.sender);
          setFriendsIds(ids);
        })
        .catch(() => {
          setFriendsIds([]);
          showErrorToast("Failed to load friends list");
        })
        .finally(() => setFriendsLoading(false));
    } else {
      setFriendsIds([]);
      setFriendsLoading(false);
    }
  }, [currentUser]); // Only depend on currentUser, not filter

  // Reset posts and pagination when filter or refreshTrigger changes
  useEffect(() => {
    setPosts([]);
    setLastVisible(null);
    setHasMore(true);
  }, [filter, refreshTrigger]);

  const fetchInitialPosts = useCallback(async () => {
    setLoading(true);
    try {
      if (currentUser && !rateLimiter.canMakeRequest(currentUser.uid)) {
        showErrorToast("Too many blog refreshes. Please wait a moment before trying again.");
        setLoading(false);
        return;
      }

      let q;
      if (filter === 'mine' && currentUser) {
        q = query(
          collection(firestore, 'BlogPost'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(POSTS_PER_PAGE)
        );
      } else if (filter === 'friends' && friendsIds.length > 0) {
        // NOTE: Firestore 'in' query supports max 10 values
        q = query(
          collection(firestore, 'BlogPost'),
          where('userId', 'in', friendsIds.slice(0, 10)),
          orderBy('timestamp', 'desc'),
          limit(POSTS_PER_PAGE)
        );
      } else if (filter === 'friends' && friendsIds.length === 0) {
        setPosts([]);
        setHasMore(false);
        setLoading(false);
        return;
      } else {
        q = query(
          collection(firestore, 'BlogPost'),
          orderBy('timestamp', 'desc'),
          limit(POSTS_PER_PAGE)
        );
      }

      const snapshot = await getDocs(q);
      const blogPosts = snapshot.docs.map(doc => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate?.() || data.timestamp || new Date();
        return { 
          id: doc.id, 
          ...data,
          timestamp 
        };
      });
      setPosts(blogPosts);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === POSTS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
      setHasMore(false);
      showErrorToast("Failed to load posts");
    }
    setLoading(false);
  }, [currentUser, filter, friendsIds]);

  // Only trigger post fetching after friends are ready
  useEffect(() => {
    if (filter !== 'friends' || (!friendsLoading && friendsIds.length >= 0)) {
      fetchInitialPosts();
    }
  }, [filter, refreshTrigger, friendsIds, friendsLoading, fetchInitialPosts]);

  const handleLoadMore = async () => {
    if (!lastVisible || !hasMore) return;
    
    if (currentUser && !rateLimiter.canMakeRequest(currentUser.uid)) {
      showErrorToast("Too many requests. Please wait a moment before trying again.");
      return;
    }

    try {
      let q;
      if (filter === 'mine' && currentUser) {
        q = query(
          collection(firestore, 'BlogPost'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(POSTS_PER_PAGE)
        );
      } else if (filter === 'friends' && friendsIds.length > 0) {
        q = query(
          collection(firestore, 'BlogPost'),
          where('userId', 'in', friendsIds.slice(0, 10)),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(POSTS_PER_PAGE)
        );
      } else {
        q = query(
          collection(firestore, 'BlogPost'),
          orderBy('timestamp', 'desc'),
          startAfter(lastVisible),
          limit(POSTS_PER_PAGE)
        );
      }

      const snapshot = await getDocs(q);
      const newPosts = snapshot.docs.map(doc => {
        const data = doc.data();
        const timestamp = data.timestamp?.toDate?.() || data.timestamp || new Date();
        return { 
          id: doc.id, 
          ...data,
          timestamp 
        };
      });
      setPosts(prevPosts => [...prevPosts, ...newPosts]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === POSTS_PER_PAGE);
    } catch (error) {
      console.error('Error loading more posts:', error);
      showErrorToast("Failed to load more posts");
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await deleteDoc(doc(firestore, 'BlogPost', postId));
      setPosts(posts.filter(post => post.id !== postId));
      showSuccessToast("Post deleted successfully");
    } catch (error) {
      showErrorToast("Failed to delete post");
    }
  };

  if (filter === 'friends' && friendsLoading) {
    return <div className="blog-feed blog-feed-loading">Loading friends...</div>;
  }
  if (filter === 'friends' && friendsIds.length === 0 && !friendsLoading) {
    return <div className="blog-feed blog-feed-empty">You have no friends' posts yet. Add friends to see their posts here!</div>;
  }
  if (loading) return <div className="blog-feed blog-feed-loading">Loading blog...</div>;
  if (!posts.length) return <div className="blog-feed blog-feed-empty">No blog posts yet.</div>;

  return (
    <div className="blog-feed">
      {posts.map(post => (
        <div className="blog-post" key={post.id}>
          <div className="blog-post-header">
            <img src={post.coverUrl} alt={post.bookTitle} className="blog-post-cover" />
            <div className="blog-post-info">
              <div className="blog-post-title">{post.bookTitle}</div>
              <div className="blog-post-meta">
                <span className="blog-post-user">{post.username}</span>
                <span className="blog-post-date">{post.timestamp?.toLocaleDateString?.() || ''}</span>
              </div>
            </div>
          </div>
          <div className="blog-post-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.postText) }} />
          <div className="blog-post-footer">
            <div className="blog-post-rating">Rating: {post.rating}/5</div>
            {post.userId === currentUser?.uid && (
              <button 
                className="delete-post-link"
                onClick={() => handleDeletePost(post.id)}
              >
                delete
              </button>
            )}
          </div>
        </div>
      ))}
      {hasMore && (
        <div className="load-more-container">
          <button className="load-more-button" onClick={handleLoadMore}>
            Load More
          </button>
        </div>
      )}
      {!hasMore && posts.length > 0 && (
        <div className="no-more-posts-message">No more posts.</div>
      )}
    </div>
  );
};

export default BlogFeed; 